/**
 * WebSocket service for real-time notifications
 */

class WebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectInterval = 5000; // 5 seconds
    this.reconnectTimer = null;
    this.pingTimer = null; // Store ping interval reference to prevent memory leak
    this.listeners = new Map();
    this.isConnecting = false;
    this.url = null;
  }

  connect(url = null) {
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
      return;
    }

    this.isConnecting = true;

    // Default WebSocket URL with JWT token authentication
    if (!url) {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = process.env.REACT_APP_API_HOST || 'localhost:8000';
      const accessToken = localStorage.getItem('accessToken');

      // Add JWT token as query parameter for authentication
      if (accessToken) {
        url = `${wsProtocol}//${host}/ws/notifications/?token=${accessToken}`;
      } else {
        url = `${wsProtocol}//${host}/ws/notifications/`;
      }
    }

    this.url = url;

    try {
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.isConnecting = false;
        this.clearReconnectTimer();
        // Start ping timer to keep connection alive (restarts on reconnect)
        this.startPing();
        this.notifyListeners('connection', { status: 'connected' });
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.isConnecting = false;
        this.notifyListeners('error', { error });
      };

      this.ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        this.isConnecting = false;
        this.ws = null;
        // Stop ping timer on disconnect (will be restarted on reconnect)
        this.stopPing();
        this.notifyListeners('connection', { status: 'disconnected' });
        this.scheduleReconnect();
      };

    } catch (error) {
      console.error('Error creating WebSocket:', error);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  disconnect() {
    this.clearReconnectTimer();
    this.stopPing();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * Check if WebSocket is currently connected
   * @returns {boolean} True if connected
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected');
    }
  }

  handleMessage(data) {
    // Notify all listeners about the message
    this.notifyListeners('message', data);

    // Handle specific message types
    switch (data.type) {
      case 'trade_execution':
        this.notifyListeners('trade_execution', data);
        break;
      case 'premium_update':
        this.notifyListeners('premium_update', data);
        break;
      case 'premium_updates':
        this.notifyListeners('premium_updates', data);
        break;
      case 'pnl_update':
        this.notifyListeners('pnl_update', data);
        break;
      case 'trigger_update':
        this.notifyListeners('trigger_update', data);
        break;
      case 'trigger_updates':
        this.notifyListeners('trigger_updates', data);
        break;
      case 'trigger_removed':
        this.notifyListeners('trigger_removed', data);
        break;
      case 'memory_state_update':
        this.notifyListeners('memory_state_update', data);
        break;
      case 'connection':
        // Already handled in onopen/onclose
        break;
      case 'pong':
        // Ping response
        break;
      default:
        console.log('Unknown message type:', data.type);
    }
  }

  scheduleReconnect() {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      console.log('Attempting to reconnect WebSocket...');
      this.connect(this.url);
    }, this.reconnectInterval);
  }

  clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  // Event listener management
  addEventListener(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);

    // Return a function to remove the listener
    return () => {
      const callbacks = this.listeners.get(event);
      if (callbacks) {
        callbacks.delete(callback);
      }
    };
  }

  removeEventListener(event, callback) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.delete(callback);
    }
  }

  notifyListeners(event, data) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in WebSocket listener:', error);
        }
      });
    }
  }

  // Ping to keep connection alive
  startPing(interval = 30000) {
    // Clear any existing ping timer to prevent memory leak
    this.stopPing();

    this.pingTimer = setInterval(() => {
      if (this.isConnected()) {
        this.send({ type: 'ping', timestamp: Date.now() });
      }
    }, interval);
  }

  // Stop the ping timer
  stopPing() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }
}

// Create singleton instance
const wsService = new WebSocketService();

export default wsService;
